export type ScenarioHistoryRow = {
    id: number;
    orderId: number;
    bookingPeriodId: number | null;
    orderPayPackageId: number;
    externalPayPackageId: number | null;
  
    jdhPostingStatusId: number | null;
    jdhStatus: string | null;
    jdhPostingId: string | number | null;
  
    startDate: string;
    endDate: string;
  
    shiftId: string | number | null;
    shiftDescription: string | null;
  
    disciplineId: number;
    discipline: string;
    disciplineAbbr: string;
    specialtyId: number;
    specialty: string;
    subSpecialtyId: number | null;
    subSpecialty: string | null;
  
    targetMarginDefault: number;
    targetMarginDayShift: number;
    targetMarginNightShift: number;
    targetMarginDayNightShift: number;
    payRangeMinMaxPercentage: number;
    defaultWeeklyPay: number;
    targetMargin: number | null;
    negotiatedContributionMargin: number | null;
  
    weeklyGrossPay: number | null;
    minimumWeeklyGrossPay: number | null;
    maximumWeeklyGrossPay: number | null;
    weeklyGrossPayRaw: number | null;
  
    billRate: number | null;
    payRate: number | null;
  
    revenue: number | null;
    grossProfit: number | null;
    grossMargin: number | null;
  
    arrivingTravel: number | null;
    endingTravel: number | null;
    interimTravel: number | null;
    arrivingAmountPerMile: number | null;
    endingAmountPerMile: number | null;
    interimAmountPerMile: number | null;
  
    interimPayPackageTimeOffId: number | null;
    amnFlight: number | null;
    amnRentalCar: number | null;
  
    vms: number | null;
    other: number | null;
    mealsAndIncidentalsPerDiem: number | null;
    lodgingPerDiem: number | null;
  
    orderPayPackageStatusId: string | number | null;
    orderPayPackageStatus: string | null;
  
    createdDate: string;
    createdById: number | null;
    lastUpdatedDate: string | null;
    updatedById: number | null;
  
    createdByFirstName: string | null;
    createdByLastName: string | null;
    updatedByFirstName: string | null;
    updatedByLastName: string | null;
  
    [k: string]: any;
  };
  
  export type Kpi = {
    metricGroup: string;
    metricName: string;
    value: number;
    display?: string;
  };
  
  export type SeriesPoint = {
    metricGroup: string;
    metricName: string;
    metricDate: string;
    value: number;
  };
  
  export type ComparisonPoint = {
    label: string;
    initial: number;
    final: number;
    change: number;
    changePct: number;
  };
  
  export type Insight = {
    question: string;
    answer: string;
    status: 'positive' | 'negative' | 'neutral';
    value: number;
    display?: string;
  };
  
  export type DashboardModel = {
    kpis: Kpi[];
    timeSeries: SeriesPoint[];
    comparisons: ComparisonPoint[];
    insights: Insight[];
    historyTable: Array<{
      createdDate: string;
      weeklyGrossPay: number | null;
      minWeeklyGrossPay: number | null;
      maxWeeklyGrossPay: number | null;
      targetMargin: number | null;
      startDate: string;
      endDate: string;
    }>;
  };
  
  const toNum = (v: any): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  
  const toDate = (iso: string | null | undefined): Date | null => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d : null;
  };
  
  const ymd = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  
  const diffDays = (a: Date, b: Date): number =>
    Math.round((b.getTime() - a.getTime()) / 86400000);
  
  const safeDiv = (n: number, d: number) => (d === 0 ? 0 : n / d);
  
  const same = (a: number | null, b: number | null) =>
    a !== null && b !== null && a === b;
  
  export function buildDashboardModel(
    rawRows: ScenarioHistoryRow[]
  ): DashboardModel {
    //Normalise + sort + limit to latest 25 items
    const rows = (rawRows ?? [])
      .map((r) => ({
        ...r,
        createdDateObj: toDate(r.createdDate),
        startDateObj: toDate(r.startDate),
        endDateObj: toDate(r.endDate),
  
        weeklyGrossPayN: toNum(r.weeklyGrossPay),
        minWeeklyGrossPayN: toNum(r.minimumWeeklyGrossPay),
        maxWeeklyGrossPayN: toNum(r.maximumWeeklyGrossPay),
        targetMarginN: toNum(r.targetMargin),
        negotiatedMarginN: toNum(r.negotiatedContributionMargin),
        grossMarginN: toNum(r.grossMargin),
        billRateN: toNum(r.billRate),
        payRateN: toNum(r.payRate),
        revenueN: toNum(r.revenue),
        grossProfitN: toNum(r.grossProfit),
        vmsN: toNum(r.vms),
        otherN: toNum(r.other),
        mealsPerDiemN: toNum(r.mealsAndIncidentalsPerDiem),
        lodgingPerDiemN: toNum(r.lodgingPerDiem),
      }))
      .filter((r) => r.createdDateObj)
      .sort(
        (a, b) =>
          b.createdDateObj!.getTime() - a.createdDateObj!.getTime() // Sort descending (newest first)
      )
      .slice(0, 25) // Take only the latest 25 items
      .reverse(); // Reverse to get chronological order (oldest to newest)
  
    if (rows.length === 0) {
      return { kpis: [], timeSeries: [], comparisons: [], insights: [], historyTable: [] };
    }
  
    const first = rows[0];
    const last = rows[rows.length - 1];
  
    //Core outcomes (THIS is the heart)
    const initialMargin = first.targetMarginN ?? 0;
    const finalMargin = last.targetMarginN ?? 0;
    const marginDelta = finalMargin - initialMargin;
  
    const initialPay = first.weeklyGrossPayN ?? 0;
    const finalPay = last.weeklyGrossPayN ?? 0;
    const payDelta = finalPay - initialPay;
  
    // Financial metrics
    const initialBillRate = first.billRateN ?? 0;
    const finalBillRate = last.billRateN ?? 0;
    const billRateDelta = finalBillRate - initialBillRate;
  
    const initialPayRate = first.payRateN ?? 0;
    const finalPayRate = last.payRateN ?? 0;
    const payRateDelta = finalPayRate - initialPayRate;
  
    const initialRevenue = first.revenueN ?? 0;
    const finalRevenue = last.revenueN ?? 0;
  
    const initialGrossProfit = first.grossProfitN ?? 0;
    const finalGrossProfit = last.grossProfitN ?? 0;
    const grossProfitDelta = finalGrossProfit - initialGrossProfit;
  
    const initialGrossMargin = first.grossMarginN ?? 0;
    const finalGrossMargin = last.grossMarginN ?? 0;
    const grossMarginDelta = finalGrossMargin - initialGrossMargin;
  
    // Calculate averages for cost components
    const avgVms = rows
      .filter(r => r.vmsN !== null)
      .reduce((sum, r, _, arr) => sum + (r.vmsN ?? 0) / arr.length, 0);
    
    const avgOther = rows
      .filter(r => r.otherN !== null)
      .reduce((sum, r, _, arr) => sum + (r.otherN ?? 0) / arr.length, 0);
  
    const avgMealsPerDiem = rows
      .filter(r => r.mealsPerDiemN !== null)
      .reduce((sum, r, _, arr) => sum + (r.mealsPerDiemN ?? 0) / arr.length, 0);
  
    const avgLodgingPerDiem = rows
      .filter(r => r.lodgingPerDiemN !== null)
      .reduce((sum, r, _, arr) => sum + (r.lodgingPerDiemN ?? 0) / arr.length, 0);
  
    // Calculate spread (bill rate - pay rate)
    const initialSpread = initialBillRate - initialPayRate;
    const finalSpread = finalBillRate - finalPayRate;
  
    //Stability detection (NOT frequency)
    let stableIterations = 1;
    for (let i = rows.length - 1; i > 0; i--) {
      if (
        same(rows[i].weeklyGrossPayN, rows[i - 1].weeklyGrossPayN) &&
        same(rows[i].targetMarginN, rows[i - 1].targetMarginN)
      ) {
        stableIterations += 1;
      } else {
        break;
      }
    }
    const isStabilised = stableIterations >= 3;
  
    //Band analysis
    const bandWidth =
      last.maxWeeklyGrossPayN !== null &&
      last.minWeeklyGrossPayN !== null
        ? last.maxWeeklyGrossPayN - last.minWeeklyGrossPayN
        : 0;
  
    const bandPosition =
      last.weeklyGrossPayN !== null &&
      last.minWeeklyGrossPayN !== null &&
      last.maxWeeklyGrossPayN !== null &&
      last.maxWeeklyGrossPayN !== last.minWeeklyGrossPayN
        ? ((last.weeklyGrossPayN - last.minWeeklyGrossPayN) * 100) /
          (last.maxWeeklyGrossPayN - last.minWeeklyGrossPayN)
        : 0;
  
    //Negotiation dependency
    const negotiatedCount = rows.filter(
      (r) => r.negotiatedMarginN !== null
    ).length;
    const negotiationPct = safeDiv(
      negotiatedCount * 100,
      rows.length
    );
  
    //Posting longevity
    const postingLongevity =
      first.startDateObj && last.endDateObj
        ? diffDays(first.startDateObj, last.endDateObj)
        : 0;
  
    // KPIs (FINAL SET – VALUE ONLY)
    const kpis: Kpi[] = [
      {
        metricGroup: "Profit Outcome",
        metricName: "Initial Target Margin (%)",
        value: initialMargin,
      },
      {
        metricGroup: "Profit Outcome",
        metricName: "Final Target Margin (%)",
        value: finalMargin,
      },
      {
        metricGroup: "Profit Outcome",
        metricName: "Margin Change (%)",
        value: marginDelta,
        display: `${marginDelta >= 0 ? "+" : ""}${marginDelta.toFixed(2)}%`,
      },
      {
        metricGroup: "Financial Performance",
        metricName: "Initial Gross Margin",
        value: initialGrossMargin,
        display: `${(initialGrossMargin * 100).toFixed(2)}%`,
      },
      {
        metricGroup: "Financial Performance",
        metricName: "Final Gross Margin",
        value: finalGrossMargin,
        display: `${(finalGrossMargin * 100).toFixed(2)}%`,
      },
      {
        metricGroup: "Financial Performance",
        metricName: "Gross Margin Change",
        value: grossMarginDelta,
        display: `${grossMarginDelta >= 0 ? "+" : ""}${(grossMarginDelta * 100).toFixed(2)}%`,
      },
      {
        metricGroup: "Financial Performance",
        metricName: "Initial Gross Profit",
        value: initialGrossProfit,
        display: `$${initialGrossProfit.toFixed(2)}`,
      },
      {
        metricGroup: "Financial Performance",
        metricName: "Final Gross Profit",
        value: finalGrossProfit,
        display: `$${finalGrossProfit.toFixed(2)}`,
      },
      {
        metricGroup: "Financial Performance",
        metricName: "Gross Profit Change",
        value: grossProfitDelta,
        display: `$${grossProfitDelta >= 0 ? "+" : ""}${grossProfitDelta.toFixed(2)}`,
      },
      {
        metricGroup: "Rate Analysis",
        metricName: "Initial Bill Rate",
        value: initialBillRate,
        display: `$${initialBillRate.toFixed(2)}`,
      },
      {
        metricGroup: "Rate Analysis",
        metricName: "Final Bill Rate",
        value: finalBillRate,
        display: `$${finalBillRate.toFixed(2)}`,
      },
      {
        metricGroup: "Rate Analysis",
        metricName: "Initial Pay Rate",
        value: initialPayRate,
        display: `$${initialPayRate.toFixed(2)}`,
      },
      {
        metricGroup: "Rate Analysis",
        metricName: "Final Pay Rate",
        value: finalPayRate,
        display: `$${finalPayRate.toFixed(2)}`,
      },
      {
        metricGroup: "Rate Analysis",
        metricName: "Initial Spread (Bill - Pay)",
        value: initialSpread,
        display: `$${initialSpread.toFixed(2)}`,
      },
      {
        metricGroup: "Rate Analysis",
        metricName: "Final Spread (Bill - Pay)",
        value: finalSpread,
        display: `$${finalSpread.toFixed(2)}`,
      },
      {
        metricGroup: "Cost Components",
        metricName: "Avg VMS",
        value: avgVms,
        display: `$${avgVms.toFixed(2)}`,
      },
      {
        metricGroup: "Cost Components",
        metricName: "Avg Other Costs",
        value: avgOther,
        display: `$${avgOther.toFixed(2)}`,
      },
      {
        metricGroup: "Cost Components",
        metricName: "Avg Meals Per Diem",
        value: avgMealsPerDiem,
        display: `$${avgMealsPerDiem.toFixed(2)}`,
      },
      {
        metricGroup: "Cost Components",
        metricName: "Avg Lodging Per Diem",
        value: avgLodgingPerDiem,
        display: `$${avgLodgingPerDiem.toFixed(2)}`,
      },
      {
        metricGroup: "Pay Pressure",
        metricName: "Weekly Pay Change",
        value: payDelta,
        display: `$${payDelta.toFixed(2)}`,
      },
      {
        metricGroup: "Stability",
        metricName: "Scenario Stabilised",
        value: isStabilised ? 1 : 0,
        display: isStabilised ? "Yes" : "No",
      },
      {
        metricGroup: "Stability",
        metricName: "Stable Iterations",
        value: stableIterations,
      },
      {
        metricGroup: "Flexibility",
        metricName: "Final Pay Band Width",
        value: bandWidth,
        display: `$${bandWidth.toFixed(0)}`,
      },
      {
        metricGroup: "Flexibility",
        metricName: "Final Pay Band Position (%)",
        value: bandPosition,
      },
      {
        metricGroup: "Governance",
        metricName: "Negotiation Dependency (%)",
        value: negotiationPct,
      },
      {
        metricGroup: "Competitiveness",
        metricName: "Posting Longevity (Days)",
        value: postingLongevity,
      },
    ];
  
    //Additional analysis for insights
    const maxPay = Math.max(...rows.map(r => r.weeklyGrossPayN ?? 0));
    const minPay = Math.min(...rows.map(r => r.weeklyGrossPayN ?? 0));
    
    // Pay band realism: Is pay band too wide or too narrow?
    const payRange = maxPay - minPay;
    const isBandRealistic = bandWidth > 0 && payRange <= bandWidth * 1.5; // Pay changes should fit within band
    
    // Default negotiation analysis
    const defaultNegotiationRatio = safeDiv(
      rows.filter(r => r.negotiatedMarginN === null && r.targetMarginN !== null).length,
      rows.length
    );
    const defaultsCausingNegotiation = defaultNegotiationRatio > 0.5;
    
    // Competitiveness vs Profitability
    const isCompetitive = bandPosition >= 40 && bandPosition <= 60; // Middle of band
    const isProfitable = finalMargin >= initialMargin * 0.95; // Margin held within 5%
    const isCompetitiveAndProfitable = isCompetitive && isProfitable;
    
    // Pay pressure on margin
    const payIncreased = payDelta > 0;
    const marginDecreased = marginDelta < 0;
    const payPushedMarginDown = payIncreased && marginDecreased;
  
    //Time series (Enhanced with more data points)
    const timeSeries: SeriesPoint[] = rows.map((r) => ({
      metricGroup: "Profit",
      metricName: "Target Margin Over Time",
      metricDate: ymd(r.createdDateObj!),
      value: r.targetMarginN ?? 0,
    }));
  
    timeSeries.push(
      ...rows.map((r) => ({
        metricGroup: "Pay",
        metricName: "Weekly Gross Pay Over Time",
        metricDate: ymd(r.createdDateObj!),
        value: r.weeklyGrossPayN ?? 0,
      }))
    );
  
    // Financial metrics time series
    rows.forEach((r) => {
      if (r.billRateN !== null) {
        timeSeries.push({
          metricGroup: "Rates",
          metricName: "Bill Rate Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: r.billRateN,
        });
      }
      if (r.payRateN !== null) {
        timeSeries.push({
          metricGroup: "Rates",
          metricName: "Pay Rate Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: r.payRateN,
        });
      }
      if (r.revenueN !== null) {
        timeSeries.push({
          metricGroup: "Financial",
          metricName: "Revenue Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: r.revenueN,
        });
      }
      if (r.grossProfitN !== null) {
        timeSeries.push({
          metricGroup: "Financial",
          metricName: "Gross Profit Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: r.grossProfitN,
        });
      }
      if (r.grossMarginN !== null) {
        timeSeries.push({
          metricGroup: "Financial",
          metricName: "Gross Margin Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: (r.grossMarginN * 100), // Convert to percentage
        });
      }
    });
  
    // Add pay band boundaries
    rows.forEach((r) => {
      if (r.minWeeklyGrossPayN !== null) {
        timeSeries.push({
          metricGroup: "Pay Band",
          metricName: "Min Weekly Gross Pay",
          metricDate: ymd(r.createdDateObj!),
          value: r.minWeeklyGrossPayN,
        });
      }
      if (r.maxWeeklyGrossPayN !== null) {
        timeSeries.push({
          metricGroup: "Pay Band",
          metricName: "Max Weekly Gross Pay",
          metricDate: ymd(r.createdDateObj!),
          value: r.maxWeeklyGrossPayN,
        });
      }
    });
  
    // Add pay band position over time
    rows.forEach((r) => {
      if (
        r.weeklyGrossPayN !== null &&
        r.minWeeklyGrossPayN !== null &&
        r.maxWeeklyGrossPayN !== null &&
        r.maxWeeklyGrossPayN !== r.minWeeklyGrossPayN
      ) {
        const pos = ((r.weeklyGrossPayN - r.minWeeklyGrossPayN) * 100) / 
                    (r.maxWeeklyGrossPayN - r.minWeeklyGrossPayN);
        timeSeries.push({
          metricGroup: "Flexibility",
          metricName: "Pay Band Position (%)",
          metricDate: ymd(r.createdDateObj!),
          value: pos,
        });
      }
    });
  
    // Add cost components over time
    rows.forEach((r) => {
      if (r.vmsN !== null) {
        timeSeries.push({
          metricGroup: "Costs",
          metricName: "VMS Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: r.vmsN,
        });
      }
      if (r.otherN !== null) {
        timeSeries.push({
          metricGroup: "Costs",
          metricName: "Other Costs Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: r.otherN,
        });
      }
      if (r.mealsPerDiemN !== null) {
        timeSeries.push({
          metricGroup: "Costs",
          metricName: "Meals Per Diem Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: r.mealsPerDiemN,
        });
      }
      if (r.lodgingPerDiemN !== null) {
        timeSeries.push({
          metricGroup: "Costs",
          metricName: "Lodging Per Diem Over Time",
          metricDate: ymd(r.createdDateObj!),
          value: r.lodgingPerDiemN,
        });
      }
    });
  
    //Comparisons (Before/After)
    const comparisons: ComparisonPoint[] = [
      {
        label: "Target Margin (%)",
        initial: initialMargin,
        final: finalMargin,
        change: marginDelta,
        changePct: initialMargin !== 0 ? safeDiv(marginDelta * 100, initialMargin) : 0,
      },
      {
        label: "Weekly Gross Pay ($)",
        initial: initialPay,
        final: finalPay,
        change: payDelta,
        changePct: initialPay !== 0 ? safeDiv(payDelta * 100, initialPay) : 0,
      },
      {
        label: "Bill Rate ($)",
        initial: initialBillRate,
        final: finalBillRate,
        change: billRateDelta,
        changePct: initialBillRate !== 0 ? safeDiv(billRateDelta * 100, initialBillRate) : 0,
      },
      {
        label: "Pay Rate ($)",
        initial: initialPayRate,
        final: finalPayRate,
        change: payRateDelta,
        changePct: initialPayRate !== 0 ? safeDiv(payRateDelta * 100, initialPayRate) : 0,
      },
      {
        label: "Gross Profit ($)",
        initial: initialGrossProfit,
        final: finalGrossProfit,
        change: grossProfitDelta,
        changePct: initialGrossProfit !== 0 ? safeDiv(grossProfitDelta * 100, initialGrossProfit) : 0,
      },
      {
        label: "Gross Margin (%)",
        initial: initialGrossMargin * 100,
        final: finalGrossMargin * 100,
        change: grossMarginDelta * 100,
        changePct: initialGrossMargin !== 0 ? safeDiv(grossMarginDelta * 100, initialGrossMargin * 100) : 0,
      },
      {
        label: "Revenue ($)",
        initial: initialRevenue,
        final: finalRevenue,
        change: finalRevenue - initialRevenue,
        changePct: initialRevenue !== 0 ? safeDiv((finalRevenue - initialRevenue) * 100, initialRevenue) : 0,
      },
    ];
  
    //Insights (Answers to business questions)
    const insights: Insight[] = [
      {
        question: "Did margin hold or erode?",
        answer: marginDelta >= 0 
          ? `Margin held strong - increased by ${marginDelta.toFixed(2)}%` 
          : `Margin eroded by ${Math.abs(marginDelta).toFixed(2)}%`,
        status: marginDelta >= 0 ? 'positive' : 'negative',
        value: marginDelta,
        display: `${marginDelta >= 0 ? '+' : ''}${marginDelta.toFixed(2)}%`,
      },
      {
        question: "Did pay push margin down?",
        answer: payPushedMarginDown
          ? "Yes - Pay increases coincided with margin decreases"
          : payIncreased && !marginDecreased
          ? "No - Pay increased while margin was maintained"
          : "No significant correlation",
        status: payPushedMarginDown ? 'negative' : 'positive',
        value: payPushedMarginDown ? 1 : 0,
      },
      {
        question: "Did the scenario stabilise?",
        answer: isStabilised
          ? `Yes - Scenario stabilised after ${stableIterations} iterations`
          : `No - Scenario still changing (${stableIterations} stable iterations)`,
        status: isStabilised ? 'positive' : 'neutral',
        value: stableIterations,
      },
      /*{
        question: "Was the pay band realistic?",
        answer: isBandRealistic
          ? `Yes - Pay band width of $${bandWidth.toFixed(0)} accommodates changes`
          : `No - Pay band may be too narrow (width: $${bandWidth.toFixed(0)})`,
        status: isBandRealistic ? 'positive' : 'negative',
        value: bandWidth,
        display: `$${bandWidth.toFixed(0)}`,
      },*/
      {
        question: "Are defaults causing negotiation?",
        answer: defaultsCausingNegotiation
          ? `Yes - ${(defaultNegotiationRatio * 100).toFixed(0)}% of iterations lack negotiation`
          : `No - Only ${(defaultNegotiationRatio * 100).toFixed(0)}% lack negotiation`,
        status: defaultsCausingNegotiation ? 'negative' : 'positive',
        value: defaultNegotiationRatio * 100,
        display: `${(defaultNegotiationRatio * 100).toFixed(0)}%`,
      },
      {
        question: "Is the posting competitive but profitable?",
        answer: isCompetitiveAndProfitable
          ? "Yes - Competitive position with maintained profitability"
          : isCompetitive && !isProfitable
          ? "Competitive but profitability eroded"
          : !isCompetitive && isProfitable
          ? "Profitable but may not be competitive"
          : "Neither competitive nor profitable",
        status: isCompetitiveAndProfitable ? 'positive' : isCompetitive || isProfitable ? 'neutral' : 'negative',
        value: isCompetitiveAndProfitable ? 1 : 0,
      },
    ];
  
    //History table
    const historyTable = rows
      .slice()
      .reverse()
      .map((r) => ({
        createdDate: r.createdDate,
        weeklyGrossPay: r.weeklyGrossPayN,
        minWeeklyGrossPay: r.minWeeklyGrossPayN,
        maxWeeklyGrossPay: r.maxWeeklyGrossPayN,
        targetMargin: r.targetMarginN,
        startDate: r.startDate,
        endDate: r.endDate,
      }));
  
    return { kpis, timeSeries, comparisons, insights, historyTable };
  }
  